import { ft } from '../core/units.js';
import { STEP_HEIGHT, ceilingHeight, penetrates, resolveHorizontal, supportHeight } from '../core/collision.js';
import { OBJECTS } from '../data/objects.js';
import { turnToward } from '../core/character.js';
import { createCarry } from './carry.js';
import { EVENT } from './events.js';

/**
 * Anything on wheels.
 *
 * The banquet cart used to be a bespoke object with a bespoke follow rule. It
 * is now one row in the catalogue, and everything that makes a cart a cart
 * lives here and applies to all of them:
 *
 *   momentum      it keeps rolling when you let go, and stops by friction
 *   slope         it accelerates down whatever it is standing on
 *   ride          you can get on it, and then it is carrying you
 *   tipping       cornering fast with a load puts it over, and spills it
 *   impact        it goes through the same bus as a thrown bottle
 *
 * None of that is authored per cart. A room service cart tips sooner than a
 * table cart because its row says 2.0 where the other says 2.6, and the loading
 * dock is a four foot drop because the loading dock is a four foot drop. There
 * is no ramp anywhere in this file and no stunt.
 */

/** Metres per second squared. The same figure everything else falls at. */
const GRAVITY = 24;

/** How far behind the player the handle sits. */
const LEASH = ft(3.4);

/** Past this, the handle slips — which is what happens when you wedge it. */
const SLIP = ft(11);

/** How much harder than the player it is allowed to move, to catch up. */
const CATCHUP = 1.25;

/**
 * Seconds the cart takes to close whatever slack is in the rope.
 *
 * Without it the cart ran at the catch-up cap until it reached the leash
 * distance exactly and then stopped dead in one frame. Against a doorframe that
 * became a stick-slip oscillation — snag, charge, stop, snag — at a pace the
 * player never touches, and a loaded cart put itself on its side going through
 * the banquet storage door at a walk. Speed proportional to slack decays to
 * nothing instead of switching off.
 */
const RESPONSE = 0.18;

/** Pushing an empty cart is still pushing a cart. */
const BASE_BURDEN = 0.15;
const LOADED_BURDEN = 0.5;

/** Metres. Tall enough to meet a wall, short enough to pass under a header. */
const BODY_HEIGHT = 1.2;

/**
 * How hard you can drive one you are standing on, and how fast it will go.
 *
 * The cap is the point. Without one a ridden cart accelerates forever, because
 * the drag here is a constant scrape rather than air resistance — thrust minus
 * friction is a net acceleration that never runs out, and a cart pointed down
 * the service corridor would arrive at the far end doing forty. Easing the
 * thrust off as it approaches the cap is what turns "hold forward" into a kick
 * and a coast.
 */
const RIDE_THRUST = 6;
const RIDE_TURN = 2.2;
const RIDE_TOP = 10.5;

/**
 * A shove with one foot, and how often you can get one in.
 *
 * This is what riding a cart is. A smooth engine that eases off as it
 * approaches the top spends the whole of a fifty foot corridor getting up to
 * speed and averages barely more than walking, which is a fair description of
 * a cart with a motor in it and a poor one of a person kicking off a floor.
 * A kick and a coast reaches a useful speed in the first second and holds it,
 * and it is also why the thing is loud.
 */
const KICK = 6.5;
const KICK_EVERY = 0.38;

/**
 * How far off a straight line a ridden cart wanders, per second, at full speed
 * and with nothing steadying you.
 *
 * A cart is two castors and a shelf and it is not meant to be stood on. Sober
 * it wanders, and the wander is what makes a corridor run at speed a bad idea:
 * it puts curvature into the path, and curvature at speed with a load on is
 * already the thing that puts a cart over. Nothing new had to be written for
 * riding to be dangerous — the wobble feeds the cornering rule that was there.
 *
 * It is a function of distance travelled rather than of time, so a given run
 * down a given corridor wanders the same way every time and a test can say so.
 */
const WOBBLE = 1.9;
const WOBBLE_WAVES = 0.42;

/**
 * The ride, for a kind whose row does not say otherwise: a foot on a floor.
 * A row with its own `drive` — a motor, a wheel — replaces any of these.
 */
const FOOT = Object.freeze({ thrust: RIDE_THRUST, top: RIDE_TOP, turn: RIDE_TURN, kick: KICK, wobble: WOBBLE, grip: 0 });
const driveOf = (cart) => ({ ...FOOT, ...(cart.definition.drive ?? {}) });

/** Metres. The seated body in a seat, across, for what it brushes past. */
const RIDER = 0.28;

/** Below this a cart is not going anywhere fast enough for a branch to matter. */
const BRUSH_SPEED = 0.6;

/** Seconds between the noises a ridden cart makes. It is not a quiet thing. */
const RIDE_NOISE_EVERY = 1.4;

/** Metres per second. Hit something this hard with a load on and it goes over. */
const IMPACT_TIP = 4.5;

/*
 * A bump, taken at speed, is a jolt: the load is thrown sideways as much as it
 * is thrown up. It goes into the same cornering measure a bend does, so it is
 * the same rule that puts the cart over and the same load that decides whether
 * it does: a step of rise metres at speed metres a second adds BUMP × rise ×
 * speed to the lean. Measured, not guessed: a full ice cart towed over a speed
 * bump on the cart road holds at 4 m/s and goes over at 5; an empty one holds
 * at anything.
 *
 * Only a kind with no motor of its own. A cart with a `drive` sits on sprung
 * wheels; a thing on castors takes the step straight into the load.
 */
const BUMP = 9.5;
/**
 * Metres of rise between which it is a bump: under the one it is the floor,
 * over the other it is a stair, which a cart on castors climbs the way a
 * person does, one riser at a time, and which was never a jolt.
 */
const BUMP_MIN = 0.03;
const BUMP_MAX = 0.12;
/** Metres per second below which a bump is walked over, not hit. */
const BUMP_SPEED = 2.5;

/** Below this a rolling cart has stopped. */
const REST = 0.15;

/**
 * How hard a corner has to be, before load, to put one over.
 *
 * Metres per second squared of sustained lateral acceleration, scaled by the
 * kind's own `tipAt`. Measured rather than guessed. Taking a right-angle corner
 * with a full load, by the pace you take it at:
 *
 *              table cart   room service cart
 *   2.4 walk      holds           holds
 *   3.6 jog       holds           holds
 *   4.2           holds           over
 *   4.8           over            over
 *
 * Empty, neither goes over at any pace a person can produce. The small cart
 * going first is the difference between 2.6 and 2.0 in the catalogue and is the
 * whole of it; there is no rule here that mentions either one.
 */
const TIP_SCALE = 2.6;

/*
 * Towing: a hitch ball on a row that says `tows`, and a drawbar on everything.
 *
 * A towed cart is not a new kind of cart and it is not moved by a new rule. It
 * is held a drawbar's length from the ball and dragged toward it like a trailer,
 * and then `settle` moves it through the building exactly as it moves anything
 * else: walls stop it, a gatepost it clips at speed with a load on puts it over,
 * and the cornering measure reads the path it actually took. A trailer cuts
 * inside the tug on a corner, so it takes the corner tighter than the tug did,
 * and that is where a loaded one goes over. Nothing below says so.
 */

/** Feet from a cart's end to the eye of its drawbar. Every cart has one. */
const TONGUE = 1.2;

/*
 * How well you have to back up to drop the pin in.
 *
 * Seven inches between the ball and the eye, and the two within twelve degrees
 * of pointing the same way. Measured against the ball and the eye rather than
 * the two middles, so what counts is the thing a person backing up is actually
 * watching over their shoulder. There is no snap and no assist: miss and the
 * cart stays where it is, and the reason you missed is what comes back.
 */
export const HITCH_GAP = ft(0.6);
export const HITCH_ANGLE = (12 * Math.PI) / 180;
/** You do not drop a pin into a moving ball. Metres per second. */
export const HITCH_STILL = 0.4;

/** Stretched this far past the drawbar, the pin pulls out. A cart jammed on a post. */
const PIN = ft(1.5);

/** Metres. How close a towed cart comes to somebody before it has hit them. */
const CLIP = 0.3;

export function createWheeled({ world, bus, entities, definitions, swipes = [], bodies = () => [] }) {
  const carts = definitions.map((entry) => {
    const kind = OBJECTS[entry.kind];
    if (!kind) throw new Error(`no wheeled kind "${entry.kind}"`);
    return {
      id: entry.id,
      kind: entry.kind,
      definition: kind,
      // `yaw` only on a row that says which way it is parked; the rest face +Z.
      home: { x: ft(entry.at[0]), z: ft(entry.at[1]), yaw: entry.yaw ?? null },
      position: { x: ft(entry.at[0]), y: 0, z: ft(entry.at[1]) },
      velocity: { x: 0, y: 0, z: 0 },
      yaw: entry.yaw ?? 0,
      state: 'parked',
      tipped: false,
      hold: createCarry(kind.hold),
      seats: (kind.seats ?? []).map((seat) => ({ ...seat, occupant: null })),
      brushing: new Set(),
      /** The cart pulling this one, and how long the drawbar is. */
      towedBy: null,
      bar: 0,
      clipping: new Set(),
    };
  });

  const byId = new Map(carts.map((cart) => [cart.id, cart]));
  /** The one in your hands or under your feet. Only ever one. */
  let active = null;
  const listeners = new Set();

  function emit(event) {
    for (const listener of listeners) listener(event);
  }

  function radius(cart) {
    return ft(cart.definition.radius) * 0.62;
  }

  /** What is on it, and who: somebody in a seat is weight like anything else. */
  function loadOf(cart) {
    const people = cart.seats.reduce((sum, seat) => sum + (seat.occupant ? seat.load ?? 0 : 0), 0);
    return Math.min(1, cart.hold.count / Math.max(1, cart.hold.capacity || 1) + people);
  }

  /**
   * The floor height the collision calls should be told about, for this kind.
   *
   * The building's own step rule is a person's: anything up to STEP_HEIGHT
   * above your feet is a step. A kind that climbs less is asked as though its
   * feet were lower by the difference, which makes exactly the steps it can
   * take steps and nothing else. A row with no `climbs` is asked at its feet,
   * which is what every cart was asked before there was a row that said.
   */
  function feetOf(cart) {
    const climbs = cart.definition.climbs;
    return climbs === undefined ? cart.position.y : cart.position.y - (STEP_HEIGHT - climbs);
  }

  /** A seat's place in the world, and which way the person in it faces. */
  function seatAt(cart, id) {
    const seat = cart.seats.find((entry) => entry.id === id);
    if (!seat) return null;
    const [lx, lz] = seat.at.map(ft);
    const sin = Math.sin(cart.yaw);
    const cos = Math.cos(cart.yaw);
    // Forward is (sin, cos); across is forward turned a quarter to the right.
    return {
      x: cart.position.x + cos * lx + sin * lz,
      y: cart.position.y + ft(seat.from),
      z: cart.position.z - sin * lx + cos * lz,
      yaw: cart.yaw + (seat.faces ?? 0),
    };
  }

  /**
   * Whether a seated body is under the roof: inside the canopy's footprint,
   * whole, and below it. Asked of the geometry every time rather than written
   * on the seat, so moving a seat or a roof moves the answer.
   */
  function covered(cart, seat) {
    const canopy = cart.definition.canopy;
    if (!canopy) return false;
    const [x0, z0, x1, z1] = canopy.rect.map(ft);
    const [lx, lz] = seat.at.map(ft);
    const inside =
      lx - RIDER >= Math.min(x0, x1) && lx + RIDER <= Math.max(x0, x1) &&
      lz - RIDER >= Math.min(z0, z1) && lz + RIDER <= Math.max(z0, z1);
    return inside && ft(canopy.at) >= ft(seat.to);
  }

  /**
   * Everything a moving cart carries somebody past.
   *
   * A swipe is a volume at rider height — a low branch, the top of a bush —
   * that the cart itself passes under or through and a person sitting on it
   * does not. Each exposed seat is a body in a band; each swipe it overlaps,
   * on the frame it starts overlapping, is an impact on whoever is sitting
   * there, through the same bus as a thrown bottle arriving somewhere. What
   * that costs them is not decided here.
   */
  function brush(cart) {
    const speed = Math.hypot(cart.velocity.x, cart.velocity.z);
    const now = new Set();
    if (speed >= BRUSH_SPEED) {
      for (const seat of cart.seats) {
        if (!seat.occupant || covered(cart, seat)) continue;
        const at = seatAt(cart, seat.id);
        const low = cart.position.y + ft(seat.from);
        const high = cart.position.y + ft(seat.to);
        for (const swipe of swipes) {
          if (high <= swipe.min.y || low >= swipe.max.y) continue;
          const cx = Math.min(Math.max(at.x, swipe.min.x), swipe.max.x);
          const cz = Math.min(Math.max(at.z, swipe.min.z), swipe.max.z);
          if (Math.hypot(at.x - cx, at.z - cz) > RIDER) continue;
          const key = `${seat.id}:${swipe.id}`;
          now.add(key);
          if (cart.brushing.has(key)) continue;
          const position = { x: cx, y: Math.max(low, swipe.min.y), z: cz };
          bus.emit(EVENT.IMPACT, {
            actor: cart.seats.find((entry) => entry.id === 'driver')?.occupant ?? 'player',
            kind: swipe.kind,
            target: { type: 'body', id: seat.occupant },
            position,
            energy: 0.8 * speed * speed,
            speed,
            broke: false,
          });
          bus.emit(EVENT.NOISE, {
            actor: 'player',
            cause: 'rustle',
            kind: swipe.kind,
            position,
            radius: ft(10) + ft(2) * speed,
          });
        }
      }
    }
    cart.brushing = now;
  }

  /**
   * Which way downhill is, here.
   *
   * Sampled rather than derived, because the floor is a pile of axis-aligned
   * boxes with no analytic surface. Four probes around the cart give a gradient
   * good enough to roll it off a loading dock, which is the only slope in the
   * building and was not put there for this.
   */
  function gradient(cart) {
    const step = radius(cart) + 0.25;
    const here = supportHeight(cart.position.x, cart.position.z, radius(cart), feetOf(cart), world);
    const at = (dx, dz) =>
      supportHeight(cart.position.x + dx, cart.position.z + dz, radius(cart), feetOf(cart), world);
    return {
      x: (at(step, 0) - at(-step, 0)) / (2 * step),
      z: (at(0, step) - at(0, -step)) / (2 * step),
      here,
    };
  }

  // --- towing -----------------------------------------------------------------

  /** The ball on the back of anything whose row says it tows, in the world. */
  function ballOf(tug) {
    const back = ft(tug.definition.tows.at);
    return {
      x: tug.position.x - Math.sin(tug.yaw) * back,
      y: tug.position.y,
      z: tug.position.z - Math.cos(tug.yaw) * back,
    };
  }

  /** Middle of a cart to the eye of its drawbar: half the deck, and the bar. */
  function reachOf(cart) {
    return ft(cart.definition.radius * 0.675 + TONGUE);
  }

  /**
   * How a tug stands to a cart it might pick up, read the way somebody backing
   * up reads it: how far the ball is from the eye, how far off to one side, and
   * how far off line. Either end of a cart has a drawbar, so the nearer one is
   * the one asked about.
   *
   * `ok` is the whole rule: close, in line, and stopped.
   */
  function hitchReading(tug, cart) {
    if (!tug?.definition.tows || !cart || cart === tug) return null;
    const ball = ballOf(tug);
    const reach = reachOf(cart);
    const forward = { x: Math.sin(tug.yaw), z: Math.cos(tug.yaw) };
    let best = null;
    for (const end of [1, -1]) {
      const axis = { x: Math.sin(cart.yaw) * end, z: Math.cos(cart.yaw) * end };
      const eye = { x: cart.position.x + axis.x * reach, z: cart.position.z + axis.z * reach };
      const dx = ball.x - eye.x;
      const dz = ball.z - eye.z;
      const gap = Math.hypot(dx, dz);
      if (best && gap >= best.gap) continue;
      // Pointing the same way as the drawbar: the tug in front of that end,
      // facing away from the cart.
      const dot = Math.max(-1, Math.min(1, forward.x * axis.x + forward.z * axis.z));
      best = {
        end,
        gap,
        // Across the drawbar's line, and along it: positive while the ball is
        // still out past the eye, which is how far there is left to back up.
        side: dx * axis.z - dz * axis.x,
        along: dx * axis.x + dz * axis.z,
        angle: Math.acos(dot),
      };
    }
    const speed = Math.hypot(tug.velocity.x, tug.velocity.z);
    return {
      ...best,
      speed,
      ok: best.gap <= HITCH_GAP && best.angle <= HITCH_ANGLE && speed <= HITCH_STILL,
    };
  }

  function towing(tug) {
    return carts.find((cart) => cart.towedBy === tug) ?? null;
  }

  function unhitch(cart, why = 'dropped') {
    if (!cart?.towedBy) return false;
    const tug = cart.towedBy;
    cart.towedBy = null;
    cart.bar = 0;
    if (cart.state === 'towed') cart.state = 'parked';
    cart.velocity.x = 0;
    cart.velocity.z = 0;
    cart.clipping = new Set();
    emit({ cart, tug, unhitched: true, why });
    return true;
  }

  /**
   * One frame of being pulled.
   *
   * The drawbar is rigid and the eye goes wherever the ball goes, so the middle
   * of the cart is put back on the circle round the ball that it was dragged off
   * — which is what a trailer's wheels do — and `settle` is asked to get it
   * there. The velocity is the step, so everything downstream of it, the jam,
   * the impact rule and the cornering measure, is reading a real movement.
   */
  function haul(cart, dt) {
    const ball = ballOf(cart.towedBy);
    const dx = cart.position.x - ball.x;
    const dz = cart.position.z - ball.z;
    const distance = Math.hypot(dx, dz);
    if (distance < 1e-4) {
      cart.velocity.x = 0;
      cart.velocity.z = 0;
      return;
    }
    const want = {
      x: ball.x + (dx / distance) * cart.bar,
      z: ball.z + (dz / distance) * cart.bar,
    };
    cart.velocity.x = (want.x - cart.position.x) / dt;
    cart.velocity.z = (want.z - cart.position.z) / dt;
    cart.yaw = Math.atan2(-dx, -dz);
  }

  /**
   * Whoever a towed cart runs into.
   *
   * People here have no collision, so being clipped is an impact on the bus
   * like a branch reaching a passenger, once per contact, and a knock anybody
   * nearby hears. What it costs is decided by whoever is listening.
   */
  function clip(cart) {
    const speed = Math.hypot(cart.velocity.x, cart.velocity.z);
    const now = new Set();
    if (speed >= BRUSH_SPEED) {
      const reach = radius(cart) + CLIP;
      for (const body of bodies()) {
        const gap = Math.hypot(body.position.x - cart.position.x, body.position.z - cart.position.z);
        if (gap > reach) continue;
        now.add(body.id);
        if (cart.clipping.has(body.id)) continue;
        const position = { x: body.position.x, y: 0.6, z: body.position.z };
        bus.emit(EVENT.IMPACT, {
          actor: 'player',
          kind: cart.kind,
          target: { type: 'body', id: body.id },
          position,
          energy: 0.5 * cart.definition.mass * speed * speed * 0.02,
          speed,
          broke: false,
          reason: 'towed',
        });
        bus.emit(EVENT.NOISE, {
          actor: 'player',
          cause: 'impact',
          kind: cart.kind,
          position,
          radius: ft(cart.definition.noise),
        });
      }
    }
    cart.clipping = now;
  }

  /** Puts it over. Everything on it lands on the floor. */
  function tip(cart, reason) {
    cart.tipped = true;
    // Over is off the ball. The pin does not hold a cart on its side.
    if (cart.towedBy) unhitch(cart, 'tipped');
    cart.state = 'parked';
    if (active === cart) active = null;
    // Everybody in it is out of it. Who they were goes with the event, so
    // whoever cares can put them on their feet.
    const thrownOut = cart.seats.filter((seat) => seat.occupant).map((seat) => seat.occupant);
    for (const seat of cart.seats) seat.occupant = null;
    cart.velocity.x = 0;
    cart.velocity.z = 0;

    const spilled = cart.hold.count;
    const kind = cart.hold.kind;
    const variant = cart.hold.variant;
    // Whatever was written down against a room is still written down against
    // it when it is on the floor of a corridor. Dropping the claim here meant a
    // cart could go over with a room's worth of linen on it and the room was
    // somehow not short of anything.
    const claim = cart.hold.claim ?? null;
    for (let i = 0; i < spilled; i += 1) {
      const angle = (i / Math.max(1, spilled)) * Math.PI * 2;
      entities.spawn({
        kind,
        variant,
        claim,
        position: {
          x: cart.position.x + Math.cos(angle) * (radius(cart) + 0.3),
          y: 0,
          z: cart.position.z + Math.sin(angle) * (radius(cart) + 0.3),
        },
      });
    }
    cart.hold.clear();

    bus.emit(EVENT.IMPACT, {
      actor: 'player',
      kind: cart.kind,
      target: { type: 'ground' },
      position: { ...cart.position },
      energy: 40,
      speed: 0,
      broke: false,
      reason,
    });
    bus.emit(EVENT.NOISE, {
      actor: 'player',
      cause: 'tip',
      kind: cart.kind,
      position: { ...cart.position },
      radius: ft(cart.definition.noise) * 2.4,
    });
    emit({ cart, tipped: true, spilled, kind, thrownOut });
  }

  /** Moves a cart, with the building winning absolutely. */
  function settle(cart, dt) {
    const r = radius(cart);
    const previous = { x: cart.position.x, z: cart.position.z };
    const feet = feetOf(cart);
    const wasStuck = penetrates(cart.position.x, cart.position.z, r, feet, cart.position.y + BODY_HEIGHT, world);

    cart.position.x += cart.velocity.x * dt;
    cart.position.z += cart.velocity.z * dt;

    const resolved = resolveHorizontal(
      { x: cart.position.x, z: cart.position.z },
      r,
      feet,
      cart.position.y + BODY_HEIGHT,
      world
    );

    /*
     * A resolve that has to move you further than your own radius has not
     * resolved anything — it has put you somewhere else.
     *
     * Walls here are six inches thick and a cart is three feet wide, so a cart
     * wedged into a corner at speed has a free side *through* the wall, and
     * the resolver will happily take it: the cart comes out the far side, not
     * penetrating anything, and the check below sees nothing wrong. It ends up
     * outside the building with the player standing on it, and the player
     * cannot get back in because the way back is the wall they came through.
     *
     * The long straight is where this finally showed up — a hundred and thirty
     * feet at eleven metres a second with a nine foot wall a yard to your left
     * — but nothing about it is new or about that corridor. Capping the
     * correction catches it wherever it happens: a legitimate resolve moves you
     * by the depth you were in by, which at this speed is a few inches.
     */
    const correction = Math.hypot(resolved.x - cart.position.x, resolved.z - cart.position.z);
    const squeezedThrough = !wasStuck && correction > r;

    cart.position.x = resolved.x;
    cart.position.z = resolved.z;

    let jammed = resolved.contacts.length > 0;
    if (
      squeezedThrough ||
      (!wasStuck && penetrates(cart.position.x, cart.position.z, r, feet, cart.position.y + BODY_HEIGHT, world))
    ) {
      cart.position.x = previous.x;
      cart.position.z = previous.z;
      jammed = true;
    }

    if (jammed) {
      const speed = Math.hypot(cart.velocity.x, cart.velocity.z);
      if (speed > 1.5) {
        bus.emit(EVENT.NOISE, {
          actor: 'player',
          cause: 'impact',
          kind: cart.kind,
          position: { ...cart.position },
          radius: ft(cart.definition.noise),
        });
      }
      /*
       * Hitting something hard with a load on puts it over.
       *
       * The same sentence as the hard-landing rule a few lines down, about the
       * other axis, and it is the one that was missing: a cart scraping a
       * doorframe at a walk must not go over, so contact cancels the cornering
       * measure entirely — which also meant a cart doing ten metres a second
       * into a corridor wall quietly stopped dead and stayed upright. Speed and
       * load decide, the way they decide everywhere else here.
       */
      if (!cart.tipped && speed > IMPACT_TIP && loadOf(cart) > 0.35) {
        cart.velocity.x = 0;
        cart.velocity.z = 0;
        tip(cart, 'impact');
        return true;
      }
      cart.velocity.x = 0;
      cart.velocity.z = 0;
    }

    // --- up and down ---------------------------------------------------------
    cart.velocity.y -= GRAVITY * dt;
    const before = cart.position.y;
    cart.position.y += cart.velocity.y * dt;
    const floor = supportHeight(cart.position.x, cart.position.z, r, feetOf(cart), world);
    if (cart.position.y <= floor && cart.velocity.y <= 0) {
      const landing = -cart.velocity.y;
      // Up a step in one frame: a bump, for the cornering measure to take.
      const rise = floor - before;
      if (rise > BUMP_MIN && rise < BUMP_MAX && !cart.definition.drive) cart.bump = rise;
      cart.position.y = floor;
      cart.velocity.y = 0;
      if (landing > 4) {
        bus.emit(EVENT.NOISE, {
          actor: 'player',
          cause: 'impact',
          kind: cart.kind,
          position: { ...cart.position },
          radius: ft(cart.definition.noise) * 1.8,
        });
        // A hard landing with a load on is how a cart goes over without anybody
        // cornering it. Same rule, different input.
        if (loadOf(cart) > 0.35 && landing > 7) tip(cart, 'landing');
      }
    } else {
      const ceiling = ceilingHeight(cart.position.x, cart.position.z, r, cart.position.y + BODY_HEIGHT, world);
      if (cart.velocity.y > 0 && cart.position.y + BODY_HEIGHT > ceiling) {
        cart.position.y = ceiling - BODY_HEIGHT;
        cart.velocity.y = 0;
      }
    }

    return jammed;
  }

  function update(dt, player) {
    // Whatever is being pulled goes after whatever is pulling it, so the ball
    // it follows is where the tug is this frame and not where it was.
    for (const cart of carts) if (!cart.towedBy) advance(cart, dt, player);
    for (const cart of carts) if (cart.towedBy) advance(cart, dt, player);
  }

  function advance(cart, dt, player) {
    {
      const wasAt = { x: cart.position.x, z: cart.position.z };

      if (cart.state === 'towed') {
        haul(cart, dt);
      } else if (cart.state === 'pushed') {
        /*
         * A rope, not a socket.
         *
         * It used to sit at a fixed offset behind the player's *facing*, which
         * meant turning on the spot swung six feet of loaded cart through a
         * half circle in a fifth of a second — and once cornering could tip a
         * cart, simply picking one up and walking away put it on its side.
         *
         * A rope only ever pulls. The cart closes on the player when the slack
         * runs out and does nothing at all when they pivot, which is both what
         * a dragged cart does and the only version in which the cornering rule
         * is measuring the player's cornering rather than their neck.
         */
        const dx = player.position.x - cart.position.x;
        const dz = player.position.z - cart.position.z;
        const distance = Math.hypot(dx, dz);
        const slack = distance - LEASH;
        if (slack > 1e-4) {
          const speed = Math.min(slack / RESPONSE, Math.max(player.speed, 1.2) * CATCHUP);
          const step = Math.min(speed * dt, slack);
          cart.velocity.x = (dx / distance) * (step / dt);
          cart.velocity.z = (dz / distance) * (step / dt);
          cart.yaw = turnToward(cart.yaw, Math.atan2(dx, dz), 6 * dt);
        } else {
          cart.velocity.x = 0;
          cart.velocity.z = 0;
        }
      } else if (cart.state === 'ridden') {
        const drive = driveOf(cart);
        const speed = Math.hypot(cart.velocity.x, cart.velocity.z);
        // Eases off as it approaches the cap, so holding forward is a shove and
        // then a coast rather than an endless engine.
        // Square root rather than linear, so the kick keeps most of its bite
        // until close to the top instead of fading over the whole run — you do
        // not push half as hard because you are half as fast.
        const headroom = Math.sqrt(Math.max(0, 1 - speed / drive.top));
        const forward = cart.rideInput?.forward ?? 0;
        const thrust = forward * drive.thrust * headroom;

        cart.sinceKick = (cart.sinceKick ?? KICK_EVERY) + dt;
        if (drive.kick > 0 && forward > 0 && speed < drive.top && cart.sinceKick >= KICK_EVERY) {
          cart.sinceKick = 0;
          const kick = drive.kick * forward * headroom;
          cart.velocity.x += Math.sin(cart.yaw) * kick;
          cart.velocity.z += Math.cos(cart.yaw) * kick;
        }

        cart.yaw += (cart.rideInput?.turn ?? 0) * drive.turn * dt;

        /*
         * And the wander, which is the whole difference between riding one of
         * these sober and riding it in a state to hold a line.
         *
         * `steady` is whatever the caller says is steadying your hand. It is
         * one number and this module has no idea where it comes from.
         */
        const steady = Math.min(1, Math.max(0, cart.rideInput?.steady ?? 0));
        cart.ridden = (cart.ridden ?? 0) + speed * dt;
        if (speed > 0.5) {
          const swing = Math.sin(cart.ridden * WOBBLE_WAVES);
          cart.yaw += swing * drive.wobble * (1 - steady) * (speed / drive.top) * dt;
        }

        cart.velocity.x += Math.sin(cart.yaw) * thrust * dt;
        cart.velocity.z += Math.cos(cart.yaw) * thrust * dt;

        /*
         * Tyres. A castor goes whichever way it is pushed, which is why a
         * ridden table cart can be turned without turning where it is going.
         * A wheel on an axle does not slide sideways, so for a kind whose row
         * says it grips, the sideways part of its speed is taken off at that
         * rate — which is what makes turning the wheel a corner, and a corner
         * is what the tipping rule below measures.
         */
        if (drive.grip > 0) {
          const hx = Math.sin(cart.yaw);
          const hz = Math.cos(cart.yaw);
          const along = cart.velocity.x * hx + cart.velocity.z * hz;
          const keep = Math.exp(-drive.grip * dt);
          const sideX = cart.velocity.x - hx * along;
          const sideZ = cart.velocity.z - hz * along;
          cart.velocity.x = hx * along + sideX * keep;
          cart.velocity.z = hz * along + sideZ * keep;
        }

        /*
         * And it is loud. A loaded cart with somebody standing on it is the
         * least discreet way to cross the property, and six corridor runs is
         * six chances for somebody to look up. Nothing here decides what that
         * costs.
         */
        cart.sinceHeard = (cart.sinceHeard ?? 0) + dt;
        if (speed > 1.5 && cart.sinceHeard >= RIDE_NOISE_EVERY) {
          cart.sinceHeard = 0;
          bus.emit(EVENT.NOISE, {
            actor: 'player',
            cause: 'ride',
            kind: cart.kind,
            // Which cart, so whoever is sitting in it can be told from whoever it went past.
            cart: cart.id,
            position: { ...cart.position },
            radius: ft(cart.definition.noise) * (2.2 + 2.6 * loadOf(cart)) * (speed / drive.top),
          });
        }
      }

      // --- momentum and the hill ------------------------------------------
      if (cart.state !== 'pushed' && cart.state !== 'towed') {
        const slope = gradient(cart);
        // Downhill is the negative gradient. A four foot dock edge reads as a
        // very large one for exactly one frame, which is the shove off it.
        cart.velocity.x -= slope.x * GRAVITY * dt * 0.5;
        cart.velocity.z -= slope.z * GRAVITY * dt * 0.5;

        const speed = Math.hypot(cart.velocity.x, cart.velocity.z);
        if (speed > 0) {
          const drag = Math.min(speed, cart.definition.friction * dt);
          cart.velocity.x -= (cart.velocity.x / speed) * drag;
          cart.velocity.z -= (cart.velocity.z / speed) * drag;
        }
        // Only a cart nobody is driving is allowed to decide it has stopped. A
        // ridden one accelerates from nothing, and a clamp that rounds anything
        // under the walking pace down to zero rounds the whole first second of
        // it away — you stand on a cart, push forward, and it sits there.
        if (cart.state !== 'ridden' && Math.hypot(cart.velocity.x, cart.velocity.z) < REST && cart.position.y <= 0.02) {
          cart.velocity.x = 0;
          cart.velocity.z = 0;
        } else if (cart.state !== 'ridden' && speed > 0.3) {
          /*
           * A cart nobody is driving faces the way it is going, which is what
           * a castor does and is why one rolling off the dock lands nose first.
           *
           * A cart somebody is *driving* does not, and the difference took a
           * while to find: this line ran on a ridden cart too, so every frame
           * the rider turned the handlebars a degree and this put them straight
           * back. Steering appeared to work — the yaw did move, dragged round
           * by the velocity it was slowly bending — at about a quarter of the
           * rate it should, which is exactly slow enough to look like a tuning
           * problem and not like a bug.
           */
          cart.yaw = Math.atan2(cart.velocity.x, cart.velocity.z);
        }
      }

      const jammed = settle(cart, dt);
      if (cart.seats.length) brush(cart);
      if (cart.state === 'towed') {
        clip(cart);
        const ball = ballOf(cart.towedBy);
        const stretched = Math.hypot(cart.position.x - ball.x, cart.position.z - ball.z) - cart.bar;
        if (stretched > PIN) unhitch(cart, 'pulled');
      }

      /*
       * Cornering, measured on the path it actually took.
       *
       * Not on the heading, which snaps to face a new waypoint, and not on the
       * velocity, which while being dragged is a synthetic number recomputed
       * from the gap to the handle every frame — a hundred-and-eighty degree
       * flip in that reads as an infinite corner. Both put every cart on its
       * side the first time anybody pushed one through a doorway.
       *
       * The position is smooth, so the angle between one frame's displacement
       * and the last is the curvature of the real path.
       */
      const step = { x: cart.position.x - wasAt.x, z: cart.position.z - wasAt.z };
      const stepLength = Math.hypot(step.x, step.z);
      const last = cart.lastStep;
      const lastLength = last ? Math.hypot(last.x, last.z) : 0;

      /*
       * A cart being scraped along a wall is not cornering. The wall is doing
       * the turning, and it turns hard: `settle` clamps one axis dead, which
       * kinks the path by ninety degrees in a single frame at walking pace and
       * reads as a corner nothing could physically take. Pushing a loaded cart
       * through a three foot doorway put it on its side every time.
       *
       * So a frame in contact with the building does not count, and neither
       * does the one after it — coming off the wall is the same kink mirrored.
       */
      if (jammed) {
        cart.lastStep = null;
        cart.lateral = 0;
      } else {
        let instant = 0;
        if (stepLength > 0.008 && lastLength > 0.008) {
          const sine = Math.abs(last.x * step.z - last.z * step.x) / (lastLength * stepLength);
          const speed = stepLength / Math.max(dt, 1e-4);
          instant = (sine * speed) / Math.max(dt, 1e-4);
        }

        /*
         * Smoothed, because tipping is a load you hold, not a load you touch.
         *
         * A cart puts a wheel up when it has been leaning for long enough to
         * get past its balance point. One frame of sideways is a bump. The
         * difference matters here more than it would in a physics engine that
         * modelled the lean, because the handle is a rope and a rope that was
         * briefly snagged catches up at almost twice walking pace — so a cart
         * that scraped a doorframe and then surged reads, for exactly one
         * sixtieth of a second, as nine metres per second squared of corner.
         * Averaged over a quarter of a second it reads as what it was.
         */
        const tau = 0.25;
        cart.lateral = (cart.lateral ?? 0) + (instant - (cart.lateral ?? 0)) * Math.min(1, dt / tau);
        if (cart.bump) {
          const speed = stepLength / Math.max(dt, 1e-4);
          if (speed > BUMP_SPEED) cart.lateral += BUMP * cart.bump * speed;
          cart.bump = 0;
        }

        if (!cart.tipped && cart.lateral * (0.35 + loadOf(cart)) > cart.definition.tipAt * TIP_SCALE) {
          tip(cart, 'cornering');
        }
        cart.lastStep = step;
      }

      // --- the handle -------------------------------------------------------
      if (cart.state === 'pushed') {
        const leash = Math.hypot(player.position.x - cart.position.x, player.position.z - cart.position.z);
        if (leash > SLIP) {
          cart.state = 'parked';
          active = null;
          emit({ cart, slipped: true });
        }
      }
      // Standing on a shelf, the wall throws you off. Sitting in a seat, it
      // stops you, which the jam already did.
      if (jammed && cart.state === 'ridden' && !cart.seats.length) {
        cart.state = 'parked';
        active = null;
        emit({ cart, thrown: true });
      }
    }
  }

  return {
    get all() {
      return carts;
    },
    get active() {
      return active;
    },
    byId: (id) => byId.get(id) ?? null,

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** Metres from a point to the nearest cart's edge, and which one. */
    nearest(point, { within = Infinity } = {}) {
      let best = null;
      for (const cart of carts) {
        const distance = Math.max(
          0,
          Math.hypot(point.x - cart.position.x, point.z - cart.position.z) - radius(cart)
        );
        if (distance > within) continue;
        if (!best || distance < best.distance) best = { cart, distance };
      }
      return best;
    },

    radius,

    grab(cart) {
      if (active || cart.state !== 'parked' || cart.tipped) return false;
      cart.state = 'pushed';
      active = cart;
      emit({ cart, pushing: true });
      return true;
    },

    /** Gets on. Only works on a kind whose row says it can be ridden. */
    /**
     * Gets on. `facing` is which way you were going as you did it.
     *
     * The cart is pointed that way, because that is what you do: you have a
     * hand on the handle, you are already walking, and you swing it in line as
     * you step on. Without it the cart keeps whatever heading trailing behind
     * somebody round a corner had left it with, and getting on means stopping
     * dead and turning it on the spot first — which costs more than the ride
     * saves, and cost exactly that for several rounds of tuning.
     */
    ride(cart, { facing = null, who = 'player' } = {}) {
      if (!cart.definition.tags.includes('rideable') || cart.tipped || cart.towedBy) return false;
      if (active && active !== cart) return false;
      cart.state = 'ridden';
      cart.rideInput = { forward: 0, turn: 0 };
      // Something with a seat is got into the way it is pointing. Something
      // you stand on is swung in line as you step on.
      const driver = cart.seats.find((seat) => seat.id === 'driver');
      if (driver) driver.occupant = who;
      else if (facing !== null) cart.yaw = facing;
      /*
       * Keep the speed you were already making, in the direction the cart is
       * actually pointing.
       *
       * Castors roll forward and scrub sideways, so the sideways half of
       * whatever it was doing goes. That is the whole of it, and it is worth
       * saying why: keeping the velocity whole meant the first shove went off
       * at an angle to it, which is a kink, and a kink under load is a corner —
       * every ride ended on its side a foot from where it started. Throwing the
       * velocity away entirely fixed that and made getting on it cost you every
       * bit of speed you had, so a running mount was worse than walking.
       */
      const rolling =
        cart.velocity.x * Math.sin(cart.yaw) + cart.velocity.z * Math.cos(cart.yaw);
      const kept = Math.max(0, rolling);
      cart.velocity.x = Math.sin(cart.yaw) * kept;
      cart.velocity.z = Math.cos(cart.yaw) * kept;
      cart.lastStep = null;
      cart.lateral = 0;
      cart.ridden = 0;
      cart.sinceKick = KICK_EVERY;
      active = cart;
      emit({ cart, riding: true });
      return true;
    },

    /**
     * Steps off, and keeps hold of the handle.
     *
     * Not the same as letting go, and the difference matters the moment riding
     * is something you do in the middle of a job: you ride the corridor, step
     * off at the door, and push it through. Without this, getting off parked
     * the cart where you stood and the rest of the shift was done in your arms.
     */
    dismount() {
      if (active?.state !== 'ridden') return false;
      // Out of a seat is out of it: there is no handle to keep hold of.
      if (active.seats.length) return this.release();
      active.state = 'pushed';
      active.rideInput = null;
      active.velocity.x = 0;
      active.velocity.z = 0;
      emit({ cart: active, pushing: true });
      return true;
    },

    release() {
      if (!active) return false;
      const cart = active;
      cart.state = 'parked';
      cart.rideInput = null;
      const driver = cart.seats.find((seat) => seat.id === 'driver');
      if (driver) driver.occupant = null;
      active = null;
      emit({ cart, parked: true });
      return true;
    },

    seatAt,
    covered: (cart, id) => {
      const seat = cart.seats.find((entry) => entry.id === id);
      return seat ? covered(cart, seat) : false;
    },

    /** A free seat that is not the driver's, or null. */
    freeSeat(cart) {
      return cart.seats.find((seat) => seat.id !== 'driver' && !seat.occupant) ?? null;
    },

    /** Puts somebody in a seat. Whoever they are is the caller's business. */
    seat(cart, id, who) {
      const seat = cart.seats.find((entry) => entry.id === id);
      if (!seat || seat.occupant || cart.tipped) return false;
      seat.occupant = who;
      return true;
    },

    /** Takes somebody out of whichever seat they are in, on any cart. */
    unseat(who) {
      for (const cart of carts) {
        for (const seat of cart.seats) {
          if (seat.occupant !== who) continue;
          seat.occupant = null;
          return { cart, seat: seat.id };
        }
      }
      return null;
    },

    /** Who is sitting where, on one cart. */
    occupants(cart) {
      return cart.seats.filter((seat) => seat.occupant).map((seat) => ({ seat: seat.id, who: seat.occupant }));
    },

    /**
     * Steering, while you are standing on one.
     *
     * `steady` is 0..1 and is somebody else's business entirely: how much
     * whatever is going on with you is helping or hurting your hands. At zero
     * the cart wanders.
     */
    steer(input) {
      if (active?.state === 'ridden') active.rideInput = input;
    },

    hitchReading,
    towing,
    ball: (tug) => (tug?.definition.tows ? ballOf(tug) : null),

    /**
     * Drops the pin in, if the tug has been backed up to it well enough.
     *
     * Refusals come back with the reading, because the reading is the lesson:
     * "a foot and a half short and twenty degrees off" is what somebody
     * standing beside the ball would shout, and it is the only help there is.
     */
    hitch(tug, cart) {
      if (!tug?.definition.tows) return { ok: false, reason: 'no-ball' };
      if (!cart || cart === tug || cart.tipped) return { ok: false, reason: 'nothing' };
      if (cart.state !== 'parked' || cart.towedBy || cart.seats.length) return { ok: false, reason: 'busy' };
      if (towing(tug)) return { ok: false, reason: 'already' };
      const reading = hitchReading(tug, cart);
      if (!reading.ok) return { ok: false, reason: 'aligned', reading };
      const ball = ballOf(tug);
      cart.towedBy = tug;
      // The bar is what it is now, so dropping the pin does not yank the cart
      // across the gap it was allowed to be off by.
      cart.bar = Math.hypot(cart.position.x - ball.x, cart.position.z - ball.z);
      cart.state = 'towed';
      cart.velocity.x = 0;
      cart.velocity.z = 0;
      cart.lastStep = null;
      cart.lateral = 0;
      cart.clipping = new Set();
      emit({ cart, tug, hitched: true });
      return { ok: true, reading };
    },

    unhitch: (cart) => unhitch(cart),

    /** Stands one back up. Anybody can right a cart. */
    upright(cart) {
      if (!cart.tipped) return false;
      cart.tipped = false;
      return true;
    },

    update,

    /** What pushing or riding it costs you. */
    burdenOf(cart) {
      return BASE_BURDEN + LOADED_BURDEN * loadOf(cart);
    },

    reset() {
      active = null;
      for (const cart of carts) {
        cart.position = { x: cart.home.x, y: 0, z: cart.home.z };
        if (cart.home.yaw !== null) cart.yaw = cart.home.yaw;
        cart.velocity = { x: 0, y: 0, z: 0 };
        cart.state = 'parked';
        cart.tipped = false;
        cart.rideInput = null;
        cart.lastStep = null;
        cart.lateral = 0;
        cart.ridden = 0;
        cart.sinceHeard = 0;
        cart.hold.clear();
        for (const seat of cart.seats) seat.occupant = null;
        cart.brushing = new Set();
        cart.towedBy = null;
        cart.bar = 0;
        cart.clipping = new Set();
      }
    },

    /** Back where it lives, empty and on its wheels. For the one cart only. */
    home(cart) {
      if (active === cart) active = null;
      if (cart.towedBy) unhitch(cart, 'home');
      cart.position = { x: cart.home.x, y: 0, z: cart.home.z };
      cart.velocity = { x: 0, y: 0, z: 0 };
      cart.yaw = cart.home.yaw ?? 0;
      cart.state = 'parked';
      cart.tipped = false;
      cart.lastStep = null;
      cart.lateral = 0;
      cart.hold.clear();
    },

    toJSON: () => ({
      active: active?.id ?? null,
      carts: carts.map((cart) => ({
        id: cart.id,
        position: cart.position,
        yaw: cart.yaw,
        state: cart.state,
        tipped: cart.tipped,
        hold: cart.hold.toJSON(),
        ...(cart.towedBy ? { towedBy: cart.towedBy.id, bar: cart.bar } : {}),
      })),
    }),

    load(state) {
      active = null;
      for (const saved of state?.carts ?? []) {
        const cart = byId.get(saved.id);
        if (!cart) continue;
        cart.position = { ...saved.position };
        cart.velocity = { x: 0, y: 0, z: 0 };
        cart.yaw = saved.yaw ?? 0;
        cart.state = saved.state === 'ridden' ? 'parked' : saved.state;
        for (const seat of cart.seats) seat.occupant = null;
        cart.tipped = Boolean(saved.tipped);
        cart.hold.load(saved.hold);
        cart.towedBy = null;
        cart.bar = 0;
        cart.clipping = new Set();
        if (cart.state === 'pushed') active = cart;
      }
      // Second pass, once every cart is where it was: a pin is between two of them.
      for (const saved of state?.carts ?? []) {
        const cart = byId.get(saved.id);
        const tug = saved.towedBy ? byId.get(saved.towedBy) : null;
        if (!cart || !tug) {
          if (cart?.state === 'towed') cart.state = 'parked';
          continue;
        }
        cart.towedBy = tug;
        cart.bar = saved.bar ?? reachOf(cart);
        cart.state = 'towed';
      }
    },
  };
}
